import { create } from 'qrcode';
import * as React from 'react';
import Svg, { Image as SvgImage, Path, Rect } from 'react-native-svg';

import { BRAND } from '@sixam/config/brand';
import { brandAssets } from '@/lib/brand-assets';

type MemberIdentityQrProps = {
  size?: number;
  value: string;
};

function createQrDrawing(value: string) {
  const qr = create(value, { errorCorrectionLevel: 'H' });
  const quietZone = 4;
  const moduleCount = qr.modules.size;
  const viewBoxSize = moduleCount + quietZone * 2;
  const logoSize = Math.max(7, Math.floor(viewBoxSize * 0.18));
  const logoPosition = (viewBoxSize - logoSize) / 2;
  const pathSegments: string[] = [];

  for (let row = 0; row < moduleCount; row += 1) {
    let col = 0;

    while (col < moduleCount) {
      if (!qr.modules.get(row, col)) {
        col += 1;
        continue;
      }

      const start = col;

      while (col < moduleCount && qr.modules.get(row, col)) {
        col += 1;
      }

      const x = start + quietZone;
      const y = row + quietZone;
      pathSegments.push(`M${x} ${y}h${col - start}v1H${x}z`);
    }
  }

  return {
    logoPosition,
    logoSize,
    pathData: pathSegments.join(''),
    viewBoxSize,
  };
}

export function MemberIdentityQr({ size = 240, value }: Readonly<MemberIdentityQrProps>) {
  const { logoPosition, logoSize, pathData, viewBoxSize } = React.useMemo(
    () => createQrDrawing(value),
    [value]
  );

  return (
    <Svg
      accessibilityLabel="Member identity QR code"
      height={size}
      role="img"
      viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
      width={size}
    >
      <Rect fill={BRAND.color.white} height={viewBoxSize} width={viewBoxSize} x={0} y={0} />
      <Path d={pathData} fill={BRAND.color.ink} />
      <Rect
        fill={BRAND.color.white}
        height={logoSize + 2}
        rx={1.5}
        width={logoSize + 2}
        x={logoPosition - 1}
        y={logoPosition - 1}
      />
      <SvgImage
        height={logoSize}
        href={brandAssets.stackedColor}
        preserveAspectRatio="xMidYMid meet"
        width={logoSize}
        x={logoPosition}
        y={logoPosition}
      />
    </Svg>
  );
}

export { createQrDrawing };
